import React, { useState } from 'react';
import { Tabs, Modal, Button, Space, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import CharterDocumentGenerator from './CharterDocumentGenerator';
import SeabobDocumentGenerator from './SeabobDocumentGenerator';
import JetskiDocumentGenerator from './JetskiDocumentGenerator';
import LunchDocumentGenerator from './LunchDocumentGenerator';
import CustomDocumentGenerator from './CustomDocumentGenerator';
import { colors, spacing, radius, typography, components } from '../../styles/monochromeTheme';

const { Title, Text } = Typography;

function UnifiedContractGenerator() {
  const [activeTab, setActiveTab] = useState('yacht');
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [lastYachtData, setLastYachtData] = useState(null);

  const handleYachtContractSuccess = (yachtData) => {
    setLastYachtData(yachtData);
    setShowAddonModal(true);
  };

  const handleAddSeabob = () => {
    setShowAddonModal(false);
    setActiveTab('seabob');
  };

  const handleAddJetski = () => {
    setShowAddonModal(false);
    setActiveTab('jetski');
  };

  const handleSkipAddons = () => {
    setShowAddonModal(false);
    setLastYachtData(null);
  };

  const tabItems = [
    {
      key: 'yacht',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>🚤</span>
          <span>Yacht Charter</span>
        </span>
      ),
      children: (
        <CharterDocumentGenerator
          onSuccess={handleYachtContractSuccess}
          initialData={lastYachtData}
        />
      ),
    },
    {
      key: 'seabob',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>🌊</span>
          <span>Seabob</span>
        </span>
      ),
      children: <SeabobDocumentGenerator initialData={lastYachtData} />,
    },
    {
      key: 'jetski',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>🏍️</span>
          <span>Jet-ski</span>
        </span>
      ),
      children: <JetskiDocumentGenerator initialData={lastYachtData} />,
    },
    {
      key: 'lunch',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>🍽️</span>
          <span>Lunch</span>
        </span>
      ),
      children: <LunchDocumentGenerator initialData={lastYachtData} />,
    },
    {
      key: 'custom',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>✏️</span>
          <span>Custom</span>
        </span>
      ),
      children: <CustomDocumentGenerator />,
    },
  ];

  // Styles
  const styles = {
    page: {
      minHeight: '100vh',
      background: colors.bgPrimary,
      padding: spacing.xxxxl,
    },
    container: {
      maxWidth: '1440px',
      margin: '0 auto',
    },
    header: {
      marginBottom: spacing.xxxxl,
      paddingBottom: spacing.xxl,
      borderBottom: `1px solid ${colors.borderLight}`,
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.xl,
    },
    headerIcon: {
      width: '56px',
      height: '56px',
      background: colors.bgDark,
      borderRadius: radius.xxl,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: typography.fontSize.xl,
    },
    headerTitle: {
      margin: 0,
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      letterSpacing: typography.letterSpacing.tight,
    },
    headerSubtitle: {
      margin: 0,
      fontSize: typography.fontSize.md,
      color: colors.textTertiary,
      marginTop: spacing.xs,
    },
    versionBadge: {
      padding: `${spacing.xs} ${spacing.md}`,
      background: colors.bgDark,
      color: colors.textInverse,
      borderRadius: radius.sm,
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.bold,
      letterSpacing: typography.letterSpacing.wide,
    },
    tabsContainer: {
      background: colors.bgSecondary,
      borderRadius: radius.xxxl,
      border: `1px solid ${colors.borderLight}`,
      overflow: 'hidden',
    },
    // Modal styles
    modalHeader: {
      background: colors.bgDark,
      padding: spacing.xxl,
      color: colors.textInverse,
    },
    modalHeaderContent: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.md,
    },
    modalHeaderIcon: {
      width: '44px',
      height: '44px',
      borderRadius: radius.lg,
      background: 'rgba(255,255,255,0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px',
    },
    modalBody: {
      padding: spacing.xxl,
    },
    modalInfoBox: {
      background: colors.bgTertiary,
      padding: spacing.lg,
      marginBottom: spacing.xl,
      borderRadius: radius.lg,
      borderLeft: `4px solid ${colors.bgDark}`,
    },
    primaryBtn: {
      height: '52px',
      borderRadius: radius.xl,
      background: colors.bgDark,
      border: 'none',
      color: colors.textInverse,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      cursor: 'pointer',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
    },
    secondaryBtn: {
      height: '48px',
      borderRadius: radius.lg,
      background: colors.bgSecondary,
      border: `2px solid ${colors.borderMedium}`,
      color: colors.textSecondary,
      fontSize: typography.fontSize.md,
      fontWeight: typography.fontWeight.medium,
      cursor: 'pointer',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.headerLeft}>
              <div style={styles.headerIcon}>📄</div>
              <div>
                <h1 style={styles.headerTitle}>Contract Generator</h1>
                <p style={styles.headerSubtitle}>Create all your documents from a single interface</p>
              </div>
            </div>
            <span style={styles.versionBadge}>V2</span>
          </div>
        </div>

        {/* Main Content with Tabs */}
        <div style={styles.tabsContainer}>
          <style>
            {`
              .monochrome-tabs .ant-tabs-nav {
                background: ${colors.bgTertiary};
                padding: 16px 20px 0 20px;
                margin-bottom: 0;
              }
              .monochrome-tabs .ant-tabs-tab {
                padding: 14px 24px !important;
                margin: 0 6px !important;
                border-radius: 14px 14px 0 0 !important;
                background: transparent;
                border: none !important;
                font-weight: 500;
                color: ${colors.textTertiary} !important;
                transition: all 0.2s ease;
                font-size: 14px;
              }
              .monochrome-tabs .ant-tabs-tab:hover {
                color: ${colors.textPrimary} !important;
                background: rgba(0,0,0,0.03);
              }
              .monochrome-tabs .ant-tabs-tab-active {
                background: ${colors.bgSecondary} !important;
                box-shadow: 0 -2px 8px rgba(0,0,0,0.03);
              }
              .monochrome-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
                color: ${colors.textPrimary} !important;
                font-weight: 600;
              }
              .monochrome-tabs .ant-tabs-ink-bar {
                display: none;
              }
              .monochrome-tabs .ant-tabs-content-holder {
                padding: 0;
                background: ${colors.bgSecondary};
              }
              .monochrome-tabs .ant-tabs-nav::before {
                border: none;
              }
            `}
          </style>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="monochrome-tabs"
            items={tabItems}
            size="large"
            tabBarGutter={8}
          />
        </div>

        {/* Add-on Modal after Yacht Contract */}
        <Modal
          title={null}
          open={showAddonModal}
          onCancel={handleSkipAddons}
          footer={null}
          width={480}
          centered
          styles={{
            content: {
              borderRadius: radius.xxxl,
              overflow: 'hidden',
              padding: 0,
            },
            body: {
              padding: 0,
            },
          }}
        >
          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <div style={styles.modalHeaderContent}>
              <div style={styles.modalHeaderIcon}>
                <PlusOutlined style={{ color: colors.textInverse }} />
              </div>
              <div>
                <Title level={4} style={{ margin: 0, color: colors.textInverse }}>
                  Add equipment?
                </Title>
                <Text style={{ color: colors.textInverseMuted, fontSize: typography.fontSize.sm }}>
                  Yacht contract created successfully
                </Text>
              </div>
            </div>
          </div>

          {/* Modal Body */}
          <div style={styles.modalBody}>
            <div style={styles.modalInfoBox}>
              <Text style={{ fontSize: typography.fontSize.md, color: colors.textPrimary }}>
                Would you like to add <strong>Seabobs</strong> or <strong>Jet-skis</strong> to this charter?
              </Text>
              <br />
              <Text style={{ color: colors.textTertiary, fontSize: typography.fontSize.sm, marginTop: spacing.sm, display: 'block' }}>
                Client information will be automatically carried over
              </Text>
            </div>

            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Button
                type="primary"
                block
                size="large"
                onClick={handleAddSeabob}
                style={styles.primaryBtn}
              >
                <span style={{ fontSize: '20px' }}>🌊</span>
                <span>Add Seabobs</span>
              </Button>

              <Button
                type="primary"
                block
                size="large"
                onClick={handleAddJetski}
                style={styles.primaryBtn}
              >
                <span style={{ fontSize: '20px' }}>🏍️</span>
                <span>Add Jet-skis</span>
              </Button>

              <Button
                block
                size="large"
                onClick={handleSkipAddons}
                style={styles.secondaryBtn}
              >
                <span>No thanks, I'm done</span>
              </Button>
            </Space>
          </div>
        </Modal>
      </div>
    </div>
  );
}

export default UnifiedContractGenerator;
